import { ButtonHTMLAttributes, FC, MouseEvent, ReactElement, useState } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useBrowserModalControls } from '@telialt/common-hooks/src/useBrowserModalControls';
import { trackSearchGA } from '@telialt/gtm-tracking/src/events';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import { SEARCH_INPUT_DROPDOWN_STATE_KEY } from '../../constants';
import { useIsLoadingSearchSuggestions } from '../../hooks/useIsLoadingSearchSuggestions';
import { SuggestionClickCallbacks } from '../../model';
import { getSearchResultsPath } from '../../utils/paths';
import { SearchSuggestions } from '../SearchSuggestions/SearchSuggestions';
import { NavBarSearch } from './NavBarSearch';

export type CustomComponents = {
    openButton?: (
        props: ButtonHTMLAttributes<HTMLButtonElement>,
    ) => ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
    closeButton?: (
        props: ButtonHTMLAttributes<HTMLButtonElement>,
    ) => ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
};

interface Props {
    components?: CustomComponents;
    navigationBarHeight?: number;
}

export const NavBarSearchConnected: FC<Props> = ({ components, navigationBarHeight }) => {
    const { t } = useTranslations();

    const [searchKeyword, setSearchKeyword] = useState('');

    const segment = useHybrisSegment();
    const isLoadingSearchSuggestions = useIsLoadingSearchSuggestions();

    const handleSearchKeywordChange = (searchText: string) => {
        setSearchKeyword(searchText);
    };

    const handleInputFocus = (searchText: string) => {
        if (searchText !== searchKeyword) {
            setSearchKeyword(searchText);
        }
    };

    const handleSearchSubmit = (searchText: string) => {
        trackSearchGA({ source: 'formSubmit', data: { searchText } });

        setSearchKeyword(searchText);
        const url = getSearchResultsPath(segment, searchText);
        window.location.assign(url);
    };

    const { closeModal: closeSearchDropdown } = useBrowserModalControls(SEARCH_INPUT_DROPDOWN_STATE_KEY);

    const cancelRedirectionToSamePlace = (_: any, event: MouseEvent) => {
        if (event.target instanceof HTMLAnchorElement && event.target.href === window.location.href) {
            event.preventDefault();
            closeSearchDropdown();
        }
    };

    const suggestionClickCallbacks: SuggestionClickCallbacks = {
        onContentSuggestionClick: cancelRedirectionToSamePlace,
        onHelpSuggestionClick: cancelRedirectionToSamePlace,
        onProductSuggestionClick: cancelRedirectionToSamePlace,
        onServiceSuggestionClick: cancelRedirectionToSamePlace,
    };

    return (
        <NavBarSearch
            minLength={0}
            onChange={handleSearchKeywordChange}
            onSubmit={handleSearchSubmit}
            onFocus={handleInputFocus}
            isLoading={isLoadingSearchSuggestions}
            content={<SearchSuggestions keyword={searchKeyword} isNavBar={true} callbacks={suggestionClickCallbacks} />}
            placeholder={t['search.results.header.input_placeholder']}
            components={components}
            navigationBarHeight={navigationBarHeight}
        />
    );
};
